/**
 * GenerateDraftQuizPanel — admin/manager-only "Generate Draft Quiz" action
 * (Phase Q1). Lives inside the training-artifact edit dialog (admin catalog
 * surface), NOT any technician Passport UI.
 *
 * Integrity: requires an AI-cost confirmation; makes ONE generate call; the
 * result is a DRAFT only (never approved, never technician-visible, never
 * enables trainingQuizMvp). Auth is enforced server-side (401/403 → readable
 * message). No service-role, no tokens exposed.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Wand2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useGenerateDraftQuiz } from '@/hooks/useGenerateDraftQuiz';

export function GenerateDraftQuizPanel({
  artifactId,
  hasTranscript,
}: {
  artifactId: string;
  hasTranscript: boolean;
}) {
  const { isGenerating, result, error, existingStatus, draftQuestions, generate, refresh } =
    useGenerateDraftQuiz(artifactId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const label = !hasTranscript
    ? 'Transcript required'
    : isGenerating
      ? 'Generating draft quiz…'
      : existingStatus === 'none'
        ? 'Generate Draft Quiz'
        : 'Regenerate Draft Quiz';

  return (
    <div className="rounded-md border p-3 space-y-2" data-testid="generate-draft-quiz-section">
      <div>
        <p className="text-sm font-medium">Knowledge-check quiz (draft)</p>
        <p className="text-xs text-muted-foreground" data-testid="quiz-existing-status">
          {existingStatus === 'approved'
            ? 'An approved quiz exists for this artifact.'
            : existingStatus === 'draft'
              ? 'A draft quiz exists (not yet approved).'
              : 'No quiz yet.'}{' '}
          Drafts are admin-only and hidden from technicians.
        </p>
      </div>

      <Button
        type="button"
        variant="outline"
        className="w-full gap-2"
        disabled={!hasTranscript || isGenerating}
        onClick={() => setConfirmOpen(true)}
        data-testid="generate-draft-quiz-button"
      >
        {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
        {label}
      </Button>

      {error && (
        <p className="text-xs text-destructive" data-testid="generate-draft-quiz-error">
          {error}
        </p>
      )}

      {result && (
        <p className="text-xs" data-testid="generate-draft-quiz-result">
          ✓ Draft created — quizId {result.quizId}, status {result.status}, {result.questionCount}{' '}
          questions. It stays hidden from technicians until approved.
        </p>
      )}

      {draftQuestions && draftQuestions.length > 0 && (
        <div
          className="mt-1 space-y-2 max-h-64 overflow-y-auto rounded border p-2"
          data-testid="draft-quiz-review"
        >
          <p className="text-xs font-semibold">Draft review (admin-only)</p>
          {draftQuestions.map((q, i) => (
            <div key={i} className="text-xs" data-testid={`draft-quiz-q-${i}`}>
              <p className="font-medium">
                {i + 1}. {q.question}
              </p>
              <ul className="ml-4 list-disc">
                {q.choices.map((c, ci) => (
                  <li
                    key={ci}
                    className={ci === q.correct_index ? 'text-green-600 font-medium' : ''}
                  >
                    {c}
                    {ci === q.correct_index ? ' ✓ correct' : ''}
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground">Why: {q.explanation}</p>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Generate a draft quiz with AI?</AlertDialogTitle>
            <AlertDialogDescription>
              This will use AI to generate a draft quiz from the artifact transcript. It may cost
              money. The draft stays hidden from technicians until you approve it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="generate-draft-quiz-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="generate-draft-quiz-confirm"
              onClick={() => {
                setConfirmOpen(false);
                void generate();
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
